import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  output,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { ChallengeHttpService } from '@core/http/challenge-http.service';
import { OrganizationHttpService } from '@core/http/organization-http.service';
import { PeopleHttpService } from '@core/http/people-http.service';
import { SessionService } from '@core/session.service';
import {
  IPageInfo,
  VirtualScrollerModule,
} from '@iharbeck/ngx-virtual-scroller';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  matAddCircleOutline,
  matCloseOutline,
  matSearchOutline,
} from '@ng-icons/material-icons/outline';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { DebounceClickDirective } from '@shared/directives/debounce-click.directive';
import { Key } from '@shared/enums/key.enum';
import {
  OrgsSearchMode,
  OrgsSearchTagFieldTypeEnum,
} from '@shared/enums/org-search-mode.enum';
import { LeadCompany } from '@shared/interfaces/lead-company.interface';
import { AutocompleteRequestInterface } from '@shared/interfaces/request/autocomplete-request.interface';
import { ApiGetResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import { ClickOutsideModule } from 'ng-click-outside';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
} from 'rxjs/operators';
import { EntityName } from '../../enums/entity-name.enum';
import { OrganizationType } from '../../enums/organization.enum';
import { ChallengeInterface } from '../../interfaces/challenge.interface';
import {
  OrganizationInterface,
  OrgSearchCustomDropdownItemInterface,
} from '../../interfaces/organization.interface';
import { UserInterface } from '../../interfaces/user.interface';

@UntilDestroy()
@Component({
  selector: 'app-organisation-search',
  templateUrl: './organisation-search.component.html',
  imports: [
    NgIcon,
    CommonModule,
    ClickOutsideModule,
    FormsModule,
    VirtualScrollerModule,
    NgbTypeaheadModule,
    TranslateModule,
    DebounceClickDirective,
  ],
  viewProviders: [
    provideIcons({ matAddCircleOutline, matCloseOutline, matSearchOutline }),
  ],
})
export class OrganisationSearchComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() userId: number;
  @Input() searchMode: OrgsSearchMode = OrgsSearchMode.ForLead;
  @Input() ignoreCompany: LeadCompany = {};
  @Input() excludedOrganisationIds: number[] = [];
  @Input() searchPlaceholder: string;
  @Input() enableCustomOnDropdown = false;
  @Input() resultContainer = '';
  @Input() organizationType: OrganizationType = OrganizationType.All;
  @Input() entityId: number;
  @Input() entityName: EntityName;
  @Input() enableDynamicHeight = true;
  @Input() height = 400;
  @Input() autoOpenDropdown = false;
  @Input() closeDropdownOnSelect = true;
  @Input() updateListOnSelect = false;
  @Input() orgsOnly = false;
  @Input() considerInnovationSpace = false;
  @Input() activeSessionRequired = false;

  createNewOrgEnabled = input(false);
  disabled = input<boolean>(false);
  selectedOrgDisplayed = input<boolean>(false);
  isSearchForEvent = input<boolean>(false);

  @Output() selectOrganisation = new EventEmitter<LeadCompany>();
  @Output() afterResetSearchText = new EventEmitter();
  createNewOrgSectionOpened = output<boolean>();

  @ViewChild('searchInput') searchInputEl: ElementRef;
  @ViewChild('searchInputModel') searchInputModel: NgModel;

  isSearching = false;
  selectedOrg: LeadCompany;
  orgsSearchTagFieldTypeEnum = OrgsSearchTagFieldTypeEnum;

  // #region AUTOCOMPLETE VIRTUAL SCROLL VARIABLES
  term$ = new BehaviorSubject<string>('');
  virtualScrollItems: (OrgSearchCustomDropdownItemInterface | LeadCompany)[] =
    [];
  totalScrollItem = 0;
  currentPageIndex = 1;
  searchText = '';
  loadingMore = true;
  isSearchInputFocused = false;
  isFirstTimeSearchOnClick = false;
  isDropdownOpen = false;
  // #endregion

  styleOnResultItem: OrgSearchCustomDropdownItemInterface[] = [
    {
      tagFieldType: OrgsSearchTagFieldTypeEnum.FillOrg,
      style: 'button',
      name: 'buttonToFillOrg',
    },
    {
      tagFieldType: OrgsSearchTagFieldTypeEnum.Header,
      style: 'header',
      name: 'suggestion',
    },
  ];

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === Key.ESC || event.key === Key.ENTER) {
      this.closeDropdown();
      this.searchInputEl.nativeElement.blur();
    }
  }

  sessionService = inject(SessionService);

  constructor(
    private readonly organizationHttpService: OrganizationHttpService,
    private readonly challengeHttpService: ChallengeHttpService,
    private readonly peopleHttpService: PeopleHttpService
  ) {}

  ngOnInit(): void {
    // register autocomlete
    this.term$
      .pipe(term => {
        return this.autocomplete(term);
      }, untilDestroyed(this))
      .subscribe(items => {
        this.virtualScrollItems = this.initVirtualScrollItems();
        this.virtualScrollItems = this.virtualScrollItems.concat(items);
      });

    globalThis.addEventListener(
      'tinymce-focus',
      this.onClickOutSide.bind(this)
    );
  }

  ngAfterViewInit(): void {
    if (this.autoOpenDropdown) {
      setTimeout(() => {
        this.isFirstTimeSearchOnClick = true;
        this.searchInputEl?.nativeElement.focus();
        this.searchInputEl?.nativeElement.click();
      }, 0);
    }
  }

  getOrg(): Observable<LeadCompany[]> {
    this.isSearching = true;
    let $apiRequest: Observable<unknown>;
    const params: AutocompleteRequestInterface = {
      pageIndex: this.currentPageIndex,
      pageSize: 20,
      keyword: this.searchText,
      ignoreIds: this.excludedOrganisationIds,
      OrgId: this.ignoreCompany?.id ?? '',
    };

    if (this.isSearchForEvent()) {
      params.entity = 'Event';
    }

    if (this.entityName === EntityName.Organization) {
      params.OrgId = this.ignoreCompany?.id ?? '';
    }

    switch (this.organizationType) {
      case OrganizationType.Organization:
        params.showAsChallenge = false;
        break;
      case OrganizationType.Challenge:
        params.showAsChallenge = true;
        break;
      case OrganizationType.All:
        break;
    }

    switch (this.searchMode) {
      case OrgsSearchMode.ForLeadIncludeChallenge:
        params.includeChallenges = true;

        if (this.isSearchForEvent()) params.entity = 'Event';

        $apiRequest = this.organizationHttpService.paginateX(
          params,
          OrgsSearchMode.ForLead
        );
        break;
      case OrgsSearchMode.ForLead:
      case OrgsSearchMode.ForLeadPerson:
        $apiRequest = this.organizationHttpService.paginateX(
          params,
          this.searchMode
        );
        break;
      case OrgsSearchMode.ForSubmitting:
      case OrgsSearchMode.ForSubmittingAll:
        if (this.orgsOnly) {
          params.showAsChallenge = false;
        }

        if (this.considerInnovationSpace) {
          params.considerInnovationSpace = this.considerInnovationSpace;
        }

        $apiRequest = this.challengeHttpService.paginateX(
          params,
          this.searchMode
        );
        break;
      case OrgsSearchMode.PresonAfferableOrgs:
        $apiRequest = this.peopleHttpService.getAfferableOrgs(
          this.userId,
          params
        );
        break;
      case OrgsSearchMode.ForFilterVenture:
        if (this.entityId && this.entityName) {
          params.entityId = this.entityId;
          params.entityName = this.entityName;
          $apiRequest = this.organizationHttpService.paginateX(
            params,
            this.searchMode
          );
        }
    }
    if (!$apiRequest) {
      return of([]);
    }

    return $apiRequest.pipe(
      finalize(() => (this.isSearching = false)),
      map((res: ApiGetResponse<OrganizationInterface | ChallengeInterface>) => {
        const items = res.items || [];
        this.totalScrollItem = res.rowCount;

        const result: LeadCompany[] = items
          .filter((item: OrganizationInterface | ChallengeInterface) => {
            return item.id !== this.ignoreCompany?.id;
          })
          .map(item => ({
            ...item,
            isMyOrganisation: item.initiators?.some(
              (initiator: UserInterface) => initiator.id === this.userId
            ),
          }));
        return result;
      })
    );
  }

  // #region AUTOCOMPLETE VIRTUAL SCROLL HANDLE
  autocomplete(text$: Observable<string>): Observable<LeadCompany[]> {
    return text$.pipe(
      filter(() => this.isSearchInputFocused),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(() => {
        this.loadingMore = true;
        this.currentPageIndex = 1;
        return this.getOrg();
      })
    );
  }

  loadMore(event: IPageInfo): void {
    if (
      event.endIndex !== this.virtualScrollItems.length - 1 ||
      this.isSearching ||
      !this.loadingMore
    ) {
      return;
    }

    if (
      (this.enableCustomOnDropdown &&
        this.totalScrollItem ===
          this.virtualScrollItems.length - this.styleOnResultItem.length) ||
      (!this.enableCustomOnDropdown &&
        this.totalScrollItem === this.virtualScrollItems.length)
    ) {
      this.loadingMore = false;
      return;
    }

    this.currentPageIndex++;
    this.getOrg()
      .pipe(untilDestroyed(this))
      .subscribe((items: LeadCompany[]) => {
        if (items?.length === 0) {
          this.loadingMore = false;
          return;
        }
        this.virtualScrollItems = this.virtualScrollItems.concat(items);
      });
  }

  initVirtualScrollItems(): (
    | OrgSearchCustomDropdownItemInterface
    | LeadCompany
  )[] {
    return this.enableCustomOnDropdown ? this.styleOnResultItem : [];
  }

  onChangeSearchText(): void {
    this.term$.next(this.searchText);
  }

  resetSearchText(): void {
    this.searchText = '';
    this.afterResetSearchText.emit();
    this.closeDropdown();

    if (this.selectedOrg && this.selectedOrgDisplayed()) {
      this.selectedOrg = null;
      this.selectOrganisation.emit(this.selectedOrg);
    }
  }

  onSelectOrg(item: OrgSearchCustomDropdownItemInterface | LeadCompany): void {
    this.selectedOrg = item;
    // Default hide ventures
    this.selectedOrg.showVentures ??= false;
    this.selectOrganisation.emit(this.selectedOrg);

    if (this.closeDropdownOnSelect) {
      this.closeDropdown();
    }

    if (this.updateListOnSelect) {
      this.virtualScrollItems = this.virtualScrollItems.filter(
        org => org.id !== item.id
      );
    }

    if (this.selectedOrg && this.selectedOrgDisplayed()) {
      this.searchText = (item as LeadCompany).orgName;
    } else {
      this.searchText = '';
    }
  }

  onClickSearchInput(): void {
    if (
      this.isSearchInputFocused &&
      this.isFirstTimeSearchOnClick &&
      !this.isSearching
    ) {
      this.loadingMore = true;
      this.currentPageIndex = 1;

      const obs = this.getOrg();
      if (obs) {
        obs.pipe(untilDestroyed(this)).subscribe((items: LeadCompany[]) => {
          this.virtualScrollItems = this.initVirtualScrollItems();
          this.virtualScrollItems = this.virtualScrollItems.concat(items);
          this.isFirstTimeSearchOnClick = false;
        });
      }
    }
  }

  onFocusSearchInput(): void {
    const isTokenExpired = this.sessionService.isTokenExpired();

    if (
      this.activeSessionRequired &&
      this.sessionService.authenticated &&
      isTokenExpired
    ) {
      this.searchInputEl.nativeElement.blur();
      this.sessionService.handleExpiringSession();
      return;
    }

    this.openDropdown();
    this.isSearchInputFocused = true;
    this.isFirstTimeSearchOnClick = true;
  }

  onBlurSearchInput(): void {
    this.isSearchInputFocused = false;
    this.isFirstTimeSearchOnClick = false;
  }
  // virtual-scroller not support dynamic height
  getDynamicHeight(): string {
    const loadingHeight = 32;
    const numberOfScrollItems = this.virtualScrollItems.length;
    const itemHeight = 53;
    // update folow styleOnResultItem
    const customDropdownHeight = 70;
    const maxNumberOfScrollItemsViewPort = this.enableCustomOnDropdown
      ? 5 + this.styleOnResultItem.length
      : 5;
    let heightResult = this.enableCustomOnDropdown
      ? customDropdownHeight - this.styleOnResultItem.length * itemHeight
      : 0;

    if (numberOfScrollItems > maxNumberOfScrollItemsViewPort) {
      heightResult += itemHeight * maxNumberOfScrollItemsViewPort;
    } else {
      heightResult += itemHeight * numberOfScrollItems;
    }
    if (this.isSearching) {
      heightResult += loadingHeight;
    }
    return `${heightResult}px`;
  }

  onClickOutSide(): void {
    this.onBlurSearchInput();
    this.closeDropdown();
  }

  openCreateNewOrgSection(): void {
    this.createNewOrgSectionOpened.emit(true);
    this.closeDropdown();
  }

  private openDropdown(): void {
    this.isDropdownOpen = true;
  }

  private closeDropdown(): void {
    this.isDropdownOpen = false;
    this.virtualScrollItems = [];
  }
  // #endregion

  ngOnDestroy(): void {
    globalThis.removeEventListener(
      'tinymce-focus',
      this.onClickOutSide.bind(this)
    );
  }
}
